import { Form, Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import useCampaigns from "../../data/useCampaigns";
import useUser from "../../data/useUser";
import useAdminUser from "../../data/useAdminUser";
import { DOMAIN_API_HOST } from "../../configs/main";
import { BalanceHistoryTable, Modal } from "../../widgets";
import { DatePicker } from "../../widgets";
import { Select } from "../../ui";
import "./BalanceHistory.scss";

const FORMIK_INITIAL_VALUES = {
  selected_campaigns: [],
  media_plan: {
    date_start: null,
    date_end: null,
  },
};

const isBetweenDate = (
  date,
  start,
  finish
) => {
  start = start?.toLocaleDateString('en-CA')
  finish = finish?.toLocaleDateString('en-CA')
  const temp = typeof date === "string" ? new Date(date) : date;
  return temp >= new Date(start) && temp <= new Date(finish);
};


function BalanceHistory() {
  const { enqueueSnackbar } = useSnackbar();
  const [formikValues, setFormikValues] = useState(FORMIK_INITIAL_VALUES);
  const { campaigns } = useCampaigns();
  const { user, mutate } = useUser();
  const { user: adminUser } = useAdminUser();
  const transaction_groups = user?.user_balance?.transaction_groups || [];
  const currentBalance = user?.user_balance?.score || 0;
  const isSuperAdmin = ["SUPERADMIN", "SUPERUSER"].includes(adminUser?.role);
  const [transactions, setTransactions] = useState(transaction_groups);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("delete");
  const [affectsBalance, setAffectsBalance] = useState(true);
  const [formState, setFormState] = useState({
    id: "",
    date: "",
    amount: "",
    type: "DEPOSIT",
    description: "",
    campaign_id: "",
    new_balance: "",
  });

  useEffect(() => {
    setTransactions(transaction_groups);
  }, [transaction_groups]);

  const transactionsWithBalance = useMemo(() => {
    if (!transactions?.length) return [];

    const sortedNewestFirst = [...transactions].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    let running = Number(currentBalance) || 0;
    const balanceById = {};

    for (const tx of sortedNewestFirst) {
      const amount = parseFloat(tx.amount) || 0;
      const balanceAfter = running;

      if (tx.type === "DEPOSIT") {
        running -= amount;
      } else if (tx.type === "CHARGE") {
        running += amount;
      }

      balanceById[tx.id] = {
        balance_after: balanceAfter,
        balance_before: running,
      };
    }

    return transactions.map((tx) => ({
      ...tx,
      balance_after: balanceById[tx.id]?.balance_after,
      balance_before: balanceById[tx.id]?.balance_before,
    }));
  }, [transactions, currentBalance]);

  let campaignsOptions = [];
  let arr = []
  if (campaigns) {
    campaigns?.map((campaign) =>
      arr.push({
        label: campaign?.basic_config?.name,
        value: campaign?.basic_config?.campaign_id,
        selected: formikValues?.selected_campaigns?.some(
          (item) => item === campaign?.basic_config?.campaign_id)
      })
    )
  } else arr.push({
    label: "Нет кампаний",
    value: "0",
    selected: false
  })
  campaignsOptions = arr

  const filteredData = useMemo(() => {
    //if (isNeedToReturnFullValue(formikValues, isLoading)) return history;

    const {
      media_plan: { date_start, date_end },
      selected_campaigns,
    } = formikValues;

    if (!transactionsWithBalance || !Array.isArray(transactionsWithBalance)) {
      return [];
    }

    const filteredByDate = transactionsWithBalance
      .slice()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .filter((item) =>
        isBetweenDate(item?.created_at.split('T')[0], date_start, date_end)
      );
    if (!selected_campaigns.length) return filteredByDate;
    const filteredByCampaigns = filteredByDate.filter((item) =>
      selected_campaigns.indexOf(item.campaign_id) !== -1
    )
    return filteredByCampaigns;
  }, [formikValues, transactionsWithBalance]);

  const summary = useMemo(() => {
    const deposits = filteredData
      .filter((item) => item.type === "DEPOSIT")
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const charges = filteredData
      .filter((item) => item.type === "CHARGE")
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    return {
      deposits,
      charges,
      total: deposits - charges,
    };
  }, [filteredData]);

  const currentUserLabel =
    adminUser?.email ||
    adminUser?.username ||
    adminUser?.first_name ||
    user?.email ||
    user?.username ||
    user?.first_name ||
    "unknown";

  const handleNewBalanceChange = (raw) => {
    if (raw === "" || raw === "-") {
      setFormState((prev) => ({ ...prev, new_balance: raw }));
      return;
    }

    if (!/^[-]?\d*$/.test(raw)) {
      return;
    }

    let sign = "";
    let digits = raw;

    if (digits.startsWith("-")) {
      sign = "-";
      digits = digits.slice(1);
    }

    digits = digits.replace(/^0+(?=\d)/, "");

    const normalized = sign + digits;
    setFormState((prev) => ({ ...prev, new_balance: normalized }));
  };

  const requestAsAdmin = async (path, method, payload) => {
    const adminToken = localStorage.getItem("admin_token");
    const response = await fetch(`${DOMAIN_API_HOST}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: "Ошибка запроса",
      }));
      throw new Error(errorData.detail || "Ошибка запроса");
    }

    return response.json().catch(() => ({}));
  };

  const handleApplyAction = async () => {
    try {
      if (!user?.id) {
        throw new Error("Не удалось определить пользователя");
      }

      if (actionType === "delete") {
        const transactionId = String(formState.id || "").trim();
        if (!transactionId) {
          throw new Error("Укажите ID транзакции");
        }

        await requestAsAdmin("/api/v1/admin/transaction-groups/manage", "POST", {
          user_id: user.id,
          action: "delete",
          transaction_id: transactionId,
          commit_to_balance: true,
        });
      }

      if (actionType === "edit") {
        const transactionId = String(formState.id || "").trim();
        if (!transactionId) {
          throw new Error("Укажите ID транзакции");
        }

        const payload = {
          user_id: user.id,
          action: "edit",
          transaction_id: transactionId,
          commit_to_balance: affectsBalance,
        };

        if (formState.amount !== "") {
          payload.amount = Number(formState.amount);
        }
        if (formState.description) {
          payload.description = formState.description;
        }
        if (formState.date) {
          payload.date = formState.date;
        }
        if (formState.campaign_id !== "") {
          payload.campaign_id = formState.campaign_id;
        }

        await requestAsAdmin("/api/v1/admin/transaction-groups/manage", "POST", payload);
      }

      if (actionType === "add") {
        if (formState.amount === "" || !formState.date) {
          throw new Error("Укажите сумму и дату");
        }

        const payload = {
          user_id: user.id,
          action: "create",
          amount: Number(formState.amount),
          transaction_type: formState.type || "DEPOSIT",
          date: formState.date,
          commit_to_balance: affectsBalance,
        };

        if (formState.description) {
          payload.description = formState.description;
        }
        if (formState.campaign_id !== "") {
          payload.campaign_id = formState.campaign_id;
        }

        await requestAsAdmin("/api/v1/admin/transaction-groups/manage", "POST", payload);
      }

      if (actionType === "change_balance") {
        if (formState.new_balance === "" || formState.new_balance === "-") {
          throw new Error("Укажите новый баланс");
        }

        await requestAsAdmin("/api/v1/admin/balances/set", "PUT", {
          user_id: user.id,
          new_score: Number(formState.new_balance),
        });
      }

      await mutate();
      setIsModalOpen(false);
      enqueueSnackbar("Изменения сохранены", {
        variant: "success",
        autoHideDuration: 2500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } catch (error) {
      enqueueSnackbar(error.message || "Не удалось сохранить изменения", {
        variant: "error",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  const resetFormState = () => {
    setFormState({
      id: "",
      date: "",
      amount: "",
      type: "DEPOSIT",
      description: "",
      campaign_id: "",
      new_balance: "",
    });
    setAffectsBalance(true);
    setActionType("delete");
  };

  const handleOpenModal = () => {
    resetFormState();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  return (
    <div className="BalanceHistory">
      <Formik
        initialValues={FORMIK_INITIAL_VALUES}
        onSubmit={(value) => {
          console.log("export") || console.log(value);
        }}
        innerRef={(formikAction) => {
          if (!formikAction) {
            return;
          }
          const { values } = formikAction;
          if (
            values.media_plan.date_start ===
            formikValues.media_plan.date_start &&
            values.media_plan.date_end === formikValues.media_plan.date_end
          ) {
            return;
          }
          values.selected_campaigns = Object.values(formikValues.selected_campaigns)
          setFormikValues(formikAction.values);
        }}
      >
        <Form className="container">
          <DatePicker />
          <Select
            placeholder={"Кампании"}
            options={campaignsOptions}
            onChange={selectedOptions => {
              setFormikValues(prevState => ({
                ...prevState,
                "selected_campaigns": selectedOptions?.map(({ value }) => value)
              })
              );
            }}
          />
        </Form>
      </Formik>
      <div className="line"></div>
      <div className="summary">
        <div className="summary-item">
          <div className="summary-label">Пополнения:</div>
          <div className="summary-value summary-value--positive">+ {summary.deposits.toFixed(2)} ₽</div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Списания:</div>
          <div className="summary-value summary-value--negative">- {summary.charges.toFixed(2)} ₽</div>
        </div>
      </div>
      {isSuperAdmin && (
        <button
          type="button"
          className="BalanceHistory-manageButton"
          onClick={handleOpenModal}
        >
          Управление транзакциями
        </button>
      )}
      <BalanceHistoryTable
        data={filteredData}
        isSuperAdmin={isSuperAdmin}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Управление транзакциями"
      >
        <div className="BalanceHistoryModal">
          <div className="BalanceHistoryModal-section">
            <div className="BalanceHistoryModal-label">Действие</div>
            <div className="BalanceHistoryModal-actions">
              <label>
                <input
                  type="radio"
                  name="actionType"
                  value="delete"
                  checked={actionType === "delete"}
                  onChange={() => setActionType("delete")}
                />
                Удалить
              </label>
              <label>
                <input
                  type="radio"
                  name="actionType"
                  value="edit"
                  checked={actionType === "edit"}
                  onChange={() => setActionType("edit")}
                />
                Редактировать
              </label>
              <label>
                <input
                  type="radio"
                  name="actionType"
                  value="add"
                  checked={actionType === "add"}
                  onChange={() => setActionType("add")}
                />
                Добавить
              </label>
              <label>
                <input
                  type="radio"
                  name="actionType"
                  value="change_balance"
                  checked={actionType === "change_balance"}
                  onChange={() => setActionType("change_balance")}
                />
                Изменить баланс
              </label>
            </div>
          </div>

          {(actionType === "delete" || actionType === "edit") && (
            <div className="BalanceHistoryModal-section">
              <div className="BalanceHistoryModal-label">ID транзакции</div>
              <input
                type="text"
                value={formState.id}
                onChange={(e) =>
                  setFormState((prev) => ({ ...prev, id: e.target.value }))
                }
                className="BalanceHistoryModal-input"
              />
            </div>
          )}

          {(actionType === "edit" || actionType === "add") && (
            <>
              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">Дата</div>
                <input
                  type="date"
                  value={formState.date}
                  onChange={(e) =>
                    setFormState((prev) => ({ ...prev, date: e.target.value }))
                  }
                  className="BalanceHistoryModal-input"
                />
              </div>

              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">Описание</div>
                <input
                  type="text"
                  value={formState.description}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="BalanceHistoryModal-input"
                />
              </div>
            </>
          )}

          {(actionType === "edit" || actionType === "add") && (
            <>
              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">
                  Учитывать в балансе
                </div>
                <label className="BalanceHistoryModal-checkbox">
                  <input
                    type="checkbox"
                    checked={affectsBalance}
                    onChange={(e) => setAffectsBalance(e.target.checked)}
                  />
                  <span>Да</span>
                </label>
              </div>

              {actionType === "add" && (
                <div className="BalanceHistoryModal-section">
                  <div className="BalanceHistoryModal-label">Тип</div>
                  <select
                    value={formState.type}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                    className="BalanceHistoryModal-input"
                  >
                    <option value="DEPOSIT">Пополнение</option>
                    <option value="CHARGE">Списание</option>
                  </select>
                </div>
              )}

              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">Сумма</div>
                <input
                  type="number"
                  value={formState.amount}
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                  className="BalanceHistoryModal-input"
                />
              </div>
            </>
          )}

          {(actionType === "add" || actionType === "edit") && (
            <div className="BalanceHistoryModal-section">
              <div className="BalanceHistoryModal-label">Кампания</div>
              <select
                value={formState.campaign_id}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    campaign_id: e.target.value,
                  }))
                }
                className="BalanceHistoryModal-input"
              >
                <option value="">Без кампании</option>
                {campaigns?.map((campaign) => (
                  <option
                    key={campaign?.basic_config?.campaign_id}
                    value={campaign?.basic_config?.campaign_id}
                  >
                    {campaign?.basic_config?.name || campaign?.basic_config?.campaign_id}
                  </option>
                ))}
              </select>
            </div>
          )}

          {actionType === "change_balance" && (
            <>
              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">
                  Текущий баланс
                </div>
                <div className="BalanceHistoryModal-balanceCurrent">
                  {currentBalance.toLocaleString("ru-RU")} ₽
                </div>
              </div>
              <div className="BalanceHistoryModal-section">
                <div className="BalanceHistoryModal-label">
                  Новый баланс
                </div>
                <input
                  type="number"
                  value={formState.new_balance}
                  onChange={(e) =>
                    handleNewBalanceChange(e.target.value)
                  }
                  className="BalanceHistoryModal-input"
                />
              </div>
            </>
          )}

          <div className="BalanceHistoryModal-footer">
            <button
              type="button"
              className="BalanceHistoryModal-button BalanceHistoryModal-button--primary"
              onClick={handleApplyAction}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="BalanceHistoryModal-button"
              onClick={handleCloseModal}
            >
              Отмена
            </button>
          </div>
          <div className="BalanceHistoryModal-meta">
            Текущее действие будет выполнено от имени: {currentUserLabel}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BalanceHistory;
